import React from "react";
import JsPDF from 'jspdf';
import JSZip from 'jszip';
import html2canvas from '@trainiac/html2canvas';
import { saveAs } from '@progress/kendo-file-saver';
import XLSX from 'xlsx';

class DocService {

    /**
     * Exports
     */

    /**
     * Exports component as PDF file.
     * @param targetElement {React.ReactNode} The component will be exported.
     * @param fileName {string} The exported file name.
     */
    exportPdf = (targetElement, fileName, onGenerateEnd = console.log) => {
        this.generatePdf(targetElement, pdf => {
            // Download pdf.
            pdf.save(`${fileName}.pdf`, {returnPromise:true}).then(onGenerateEnd);
        })
    };

    /**
     * Exports component as multi page PDF file.
     * @param targetElements Array of {React.ReactNode} The component will be exported.
     * @param fileName {string} The exported file name.
     */
    exportMultiPagePdf = (targetElements, fileName, onGenerateEnd = console.log) => {
        this.generateMultiPagePdf(targetElements, pdf => {
            // Download pdf.
            pdf.save(`${fileName}.pdf`, {returnPromise:true}).then(onGenerateEnd);
        })
    };

    /**
     * Exports component as PNG file.
     * @param targetElement {React.ReactNode} The component will be exported.
     * @param fileName {string} The exported file name.
     */
    exportPng = (targetElement, fileName,  onGenerateEnd = console.log) => {
        this.createBlob(targetElement, (blob) => {
            saveAs(blob, `${fileName}.png`);
            onGenerateEnd();
        });
    };

    /**
     * Exports component as JPG file.
     * @param targetElement {React.ReactNode} The component will be exported.
     * @param fileName {string} The exported file name.
     */
    exportJpg = (targetElement, fileName, onGenerateEnd = console.log) => {
        this.createBlob(targetElement, (blob) => {
            saveAs(blob, `${fileName}.jpg`);
            onGenerateEnd();
        });
    };

    /**
     * Exports component as Excel/Csv sheet file.
     * @param tableId {string} The html table id that will be exported.
     * @param fileName {string} The exported file name.
     * @param isCsv {bool} Is exported file .csv or not.
     */
    exportSheet = (tableId, fileName, isCsv = false, onGenerateEnd = console.log) => {
        // Table to WorkSheet
        const worksheet = XLSX.utils.table_to_sheet(document.getElementById(tableId));
        // Create new WorkBook
        const workbook = XLSX.utils.book_new();
        // Add WorkSheet to WorkBook
        XLSX.utils.book_append_sheet(workbook, worksheet, fileName);
        // Write data to Excel file
        const date = new Date();
        const ext = isCsv ? 'csv' : 'xlsx';
        
        onGenerateEnd();
        XLSX.writeFile(workbook, `${fileName}-${date.toLocaleDateString()}.${ext}`, { type: 'binary', bookType: ext });
    };

    /**
     * Generates
     */

    generatePdf = (targetElement, callback) => {
        // Draw component on canvas.
        this.createCanvas(targetElement, canvas => {
            // Convert canvas to image.
            const imageData = canvas.toDataURL('image/png');
            // Create pdf document. params: portrait, points, width, height, compressPdf.
            const pdf = new JsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height],
                compressPdf: false
            });

            const width = pdf.internal.pageSize.getWidth();
            const height = pdf.internal.pageSize.getHeight();

            // Add image to pdf document.
            pdf.addImage(imageData, 'PNG', 0, 0, width, height, undefined, 'FAST');

            callback(pdf);
        });
    }

    generateMultiPagePdf = (targetElements, callback) => {
        let count = 0;

        // Create pdf document. params: portrait, points, width, height, compressPdf.
        const pdf = new JsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: [targetElements[0]?.offsetWidth, targetElements[0]?.offsetHeight],
            compressPdf: false
        });

        targetElements.map(targetElement => {
            this.createCanvas(targetElement, canvas => {
                count++;

                // Convert canvas to image.
                const imageData = canvas.toDataURL('image/png');
                
                
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                
                // pdf.internal.pageSize.setWidth(pageWidth);
                // pdf.internal.pageSize.setHeight(pageHeight);
                pdf.addPage();
                
                // Add image to pdf document.
                pdf.addImage(imageData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');

                if(targetElements.length === count) {
                    pdf.deletePage(1); // Delete First Blank Page
                    callback(pdf);
                }
            })
        });
    }

    /**
     * Creates
     */

    /**
     * Creates blob from component.
     * @param targetElement {React.ReactNode} The component will be converted.
     * @param callback {function} The callback fires when complete converting process.
     */
    createBlob = (targetElement, callback) => {
        this.createCanvas(targetElement, (canvas) => {
            canvas.toBlob((blob) => {
                if (typeof callback === 'function')
                    callback(blob);
            });
        });
    };

    /**
     * Draws component on canvas.
     * @param targetElement {React.ReactNode} The component will be drawn.
     * @param callback {function} The callback fires when complete drawing process.
     */
    createCanvas = (targetElement, callback) => {
        html2canvas(targetElement, {
            allowTaint: true,
            logging: true,
            scale: 2, // To increase resolution (width: 600 * 2 = 1200px).
            scrollY: 0,
        }).then((canvas) => {
            const ctx = canvas.getContext('2d');
            ctx.webkitImageSmoothingEnabled = false;
            ctx.mozImageSmoothingEnabled = false;
            ctx.imageSmoothingEnabled = false;

            if (typeof callback === 'function')
                callback(canvas);
        });
    };


    
    /**
     * Zip
     */
    generateZip = (files, fileType, callback = console.log) => {
        const generateFn = this.generateFns[fileType];
        const zip = new JSZip();
        let c = 0;

        files.map((file, idx) => {
            //The exportFn() function returns a jsPDF
            generateFn(file.targetElement, result => {
                // In case of PDF, jsPdf output blob
                const blob = fileType === 'pdf' ? result.output('blob') : result;

                if (typeof result !== 'undefined') {
                    try {
                        zip.file(`${file?.name}_${idx}.${fileType}`, blob);
                    }
                    catch {
                        console.error('Something went wrong!');
                    }
                }
                c++;
                if(files.length === c) {
                    callback(zip);
                }
            });
        })
    }

    /**
     * Export Zip
     */
    exportZip = (files, fileType, fileName, callback = console.log) => {
        this.generateZip(files, fileType, (zip) => {
            zip.generateAsync({type:'blob'}).then((zipContent) => {
                saveAs(zipContent, `${fileName}.zip`);
                callback('ZIP GENERATED');
            });
        })
    }

    generateFns = {
        pdf: this.generatePdf,
        jpg: this.createBlob,
        png: this.createBlob,
    }
}

const Doc = new DocService();
export default Doc;
